<!-- eslint-disable vue/define-emits-declaration -->
<!-- eslint-disable vue/custom-event-name-casing -->
<!-- eslint-disable vue/require-typed-ref -->
<!-- eslint-disable @typescript-eslint/no-unnecessary-condition -->
<!-- eslint-disable @typescript-eslint/prefer-nullish-coalescing -->
<script setup lang="ts">
import { useFormContext } from 'vee-validate'
import { computed, type ComputedRef, onMounted, ref, watch, watchEffect } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { ManagementSuggestionsContext } from '@/composables/organization/useManagementSuggestions'
import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import AiNoResultsBox from '@/components/common/ui/AiNoResultsBox.vue'
import AiSuggestionBox from '@/components/common/ui/AiSuggestionBox.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import TWTextArea from '@/components/common/ui/inputs/TWTextArea.vue'
import ManagementSuggestionList from '@/components/editRelationGroup/managementSuggestions/ManagementSuggestionList.vue'
import SectionAddresses from '@/components/editRelationGroup/sections/SectionAddresses.vue'
import SectionBankAccountOrganization from '@/components/editRelationGroup/sections/SectionBankAccountOrganization.vue'
import SectionEmails from '@/components/editRelationGroup/sections/SectionEmails.vue'
import SectionGeneralDataOrganization from '@/components/editRelationGroup/sections/SectionGeneralDataOrganization.vue'
import SectionImageUpload from '@/components/editRelationGroup/sections/SectionImageUpload.vue'
import SectionMetaData from '@/components/editRelationGroup/sections/SectionMetaData.vue'
import SectionPhoneNumbers from '@/components/editRelationGroup/sections/SectionPhoneNumbers.vue'
import SectionWeblinks from '@/components/editRelationGroup/sections/SectionWeblinks.vue'
import { useBankOptions } from '@/composables/useBankOptions'
import { useTagOptions } from '@/composables/useTagOptions'
import { RelationType } from '@/constants/relations'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { hasAnyAiData } from '@/utilities/organizationAi'
import { translationFunctionDefault } from '@/utilities/translations'

import CheckBox from '../common/ui/inputs/CheckBox.vue'
import MultiSelect from '../common/ui/inputs/MultiSelect.vue'
import IconSparkles from '../icons/IconSparkles.vue'

interface CreateOrganizationFormLongProps {
  aiData: null | TWOrganizationAIResponse
  doctypeOptions: DropdownOption<RelationType>[]
  aiLoading?: boolean | undefined
  ibanErrorMessage?: string | undefined
  isPersonCustomer?: boolean
  managementSuggestions?: ManagementSuggestionsContext | undefined
}

const props = defineProps<CreateOrganizationFormLongProps>()

const emit = defineEmits<{
  aiCancel: []
  aiClick: [aiString: string]
  aiFieldsStillVisibleChanged: [value: boolean]
  nameChange: []
  'update:isPersonCustomer': [value: boolean]
}>()

const onPersonCustomerChange = (): void => {
  emit('update:isPersonCustomer', !props.isPersonCustomer)
}

const onNameChange = (): void => {
  emit('nameChange')
}

let noAiDataAvailable: ComputedRef<boolean> = computed(() => false)

const checkForAiData = (): void => {
  noAiDataAvailable = computed(() => {
    if (!props.aiData) {
      return false
    } else if (Object.keys(props.aiData).length === 0) {
      return true
    }

    return !hasAnyAiData(props.aiData)
  })
}

const handleAiCancel = (): void => {
  emit('aiCancel')
}

const handleAiClick = (aiString: string): void => {
  emit('aiClick', aiString)
  checkForAiData()
}

const tfDefault = translationFunctionDefault()
const { values } = useFormContext()
const investorTypeOptions = ref<{ optionLabel: string; optionValue: string }[]>([])
const otherTypeOptions = ref<{ optionLabel: string; optionValue: string }[]>([])
const customAPIStore = useFrappeNestCustomAPIStore()
// eslint-disable-next-line @typescript-eslint/unbound-method
const { bankOptions, loadBankOptions } = useBankOptions()

const { canCreateTag, createNewTagOption, tagOptions } = useTagOptions()

const isAiImageFilled = ref(!!props.aiData?.logo_url)
const aiFieldsStillVisible = ref(false)

const triggerManualInput = (): void => {
  isAiImageFilled.value = false
}

const logoRef = ref()
const generalRef = ref()
const weblinksRef = ref()
const emailsRef = ref()
const phonesRef = ref()
const addressesRef = ref()

const handleClearAIFields = (): void => {
  generalRef.value?.clearAIFields()
  weblinksRef.value?.clearAIFields()
  emailsRef.value?.clearAIFields()
  phonesRef.value?.clearAIFields()
  addressesRef.value?.clearAIFields()
  isAiImageFilled.value = false
}

const handleAcceptAIFields = (): void => {
  generalRef.value?.acceptAIFields()
  weblinksRef.value?.acceptAIFields()
  emailsRef.value?.acceptAIFields()
  phonesRef.value?.acceptAIFields()
  addressesRef.value?.acceptAIFields()
  isAiImageFilled.value = false
}

onMounted(async () => {
  checkForAiData()
  try {
    const types = await customAPIStore.getTWInvestorType()
    investorTypeOptions.value = types.map((it) => ({
      optionLabel: tfDefault(`investor.information.investor_type_options.${it.name}`),
      optionValue: it.name,
    }))
  } catch {
    /* empty */
  }
  try {
    const otypes = await customAPIStore.getTWOtherType()
    otherTypeOptions.value = otypes.map((ot) => ({ optionLabel: ot.name, optionValue: ot.name }))
  } catch {
    /* empty */
  }

  // eslint-disable-next-line no-void
  void loadBankOptions()
})

watch(aiFieldsStillVisible, (newVal) => {
  emit('aiFieldsStillVisibleChanged', newVal)
})

watch(
  () => props.aiData?.logo_url,
  (newVal) => {
    if (newVal) {
      isAiImageFilled.value = true
    }
  },
)

watchEffect(() => {
  aiFieldsStillVisible.value =
    isAiImageFilled.value ||
    !!generalRef.value?.isAiFieldsStillVisible() ||
    !!weblinksRef.value?.isAiFieldsStillVisible() ||
    !!emailsRef.value?.isAiFieldsStillVisible() ||
    !!phonesRef.value?.isAiFieldsStillVisible() ||
    !!addressesRef.value?.isAiFieldsStillVisible()
})
</script>

<template>
  <div>
    <div class="space-y-5 px-8">
      <SingleSelect
        :data-testid="TEST_IDS_CREATE.createOrganizationForm.organizationType"
        :label="tfDefault('createOrganization.labelRelationType')"
        name="doctype"
        on-white-bg
        :options="[doctypeOptions]"
        required
      />
      <template v-if="values['doctype'] === RelationType.other">
        <SingleSelect
          :label="tfDefault('other.information.type')"
          name="type"
          on-white-bg
          :options="[otherTypeOptions]"
          :placeholder="tfDefault('other.information.noType')"
        />
      </template>
      <template v-if="values['doctype'] === RelationType.investors">
        <SingleSelect
          :label="tfDefault('investor.information.investor_type')"
          name="tw_investor_type"
          on-white-bg
          :options="[investorTypeOptions]"
          :placeholder="tfDefault('investor.information.noInvestorType')"
        />
        <InputGrid>
          <TWTextArea
            in-create-context
            :label="tfDefault('investor.information.loanDescription')"
            name="tw_description"
            on-white-bg
          />
          <TextInput
            in-create-context
            is-number
            :label="tfDefault('investor.information.totalLoanAmount')"
            name="tw_total_loan_amount"
            on-white-bg
          />
        </InputGrid>
      </template>
      <TextInput
        v-if="values['doctype'] === RelationType.salesPartners"
        in-create-context
        is-number
        :label="tfDefault('salesPartner.details.commissionRate')"
        name="commission_rate"
        on-white-bg
      />
      <CheckBox
        v-if="values['doctype'] === RelationType.customers"
        id="is-person-customer-long"
        :checked="props.isPersonCustomer ?? false"
        :label-text="tfDefault('createOrganization.isPersonCustomerCheckbox')"
        @change="onPersonCustomerChange"
      />
    </div>

    <SectionMetaData class="border-none" />
    <div class="relative flex justify-start border-b border-dotted border-b-black-200 px-8 pb-7">
      <IconSparkles
        v-if="isAiImageFilled"
        class="absolute left-28 top-2 z-20 size-5 text-lochmara-600"
      />
      <SectionImageUpload
        ref="logoRef"
        :ai-image-url="aiData?.logo_url"
        :label-text="
          props.isPersonCustomer
            ? tfDefault('createOrganization.imageUploadTextPerson')
            : tfDefault('createOrganization.imageUploadText')
        "
        name="tw_image"
        @manual-input="triggerManualInput"
      />
    </div>
    <SectionGeneralDataOrganization
      ref="generalRef"
      :ai-data="aiData"
      :ai-loading="aiLoading"
      in-create-context
      :is-person-customer="props.isPersonCustomer ?? false"
      @ai-cancel="handleAiCancel"
      @ai-click="handleAiClick"
      @name-change="onNameChange"
    />
    <CollapsableContent :title="tfDefault('createRelation.sectionTitleTags')">
      <MultiSelect
        allow-adding-pills
        :data-testid="TEST_IDS_CREATE.tagSelect"
        name="tw_user_tags"
        :on-create-new-pill="canCreateTag ? createNewTagOption : undefined"
        on-white-bg
        :options="[{ options: tagOptions }]"
        placeholder="Select tags"
      />
    </CollapsableContent>
    <SectionBankAccountOrganization
      :bank-options="bankOptions"
      :iban-error-message="props.ibanErrorMessage"
      in-create-context
    />
    <SectionWeblinks ref="weblinksRef" :ai-data="aiData" in-create-context />
    <SectionEmails
      ref="emailsRef"
      :ai-data="aiData"
      :allow-invoice-email="values['doctype'] === RelationType.customers"
      in-create-context
    />
    <SectionPhoneNumbers ref="phonesRef" :ai-data="aiData" in-create-context />
    <SectionAddresses
      ref="addressesRef"
      :ai-data="aiData"
      has-alternative-recipient
      in-create-context
      :is-person-customer="props.isPersonCustomer ?? false"
    />
    <!-- Before the company data box, so the suggestions are reviewed before the final decisions -->
    <ManagementSuggestionList
      v-if="managementSuggestions"
      :context="managementSuggestions"
      mode="create"
    />
    <AiSuggestionBox
      :show="aiFieldsStillVisible"
      @accept-ai-fields="handleAcceptAIFields"
      @clear-ai-fields="handleClearAIFields"
    />
    <AiNoResultsBox :show="noAiDataAvailable ?? false" />
  </div>
</template>
